<script>

  import { onMount, tick } from 'svelte'

  import { parse, parseUrl } from 'query-string'

  import { formlookupresult,
           formlookupfldvals,
           currentformid,
           fldvalselected,
           formfldvalselected,
           pagectxstate } from './stores'

  import FormActions from './FormActions.svelte'
  import FormFieldTable,
         { isRowMatch } from './FormFieldTable.svelte'

  import { getApiUrl, showPage, fetchWithSession } from './util.js'

  let mounted

  let formid
  let tablewrap 

  let errorMessage

  let selectedRow


  $: { handlePageCtxStateChange($pagectxstate) }
  async function handlePageCtxStateChange(pgctxstate) {
    if (mounted) {
      const parsedurl = parseUrl(pgctxstate.path)
      console.log(parsedurl)
      if (parsedurl.query.frm !== formid ||
          $currentformid !== formid) {
        doFormLookup()
      }
    }
  }


  export async function doFormLookup() {

    const querystring = location.search
    formid = parse(querystring).frm

    if (!formid) {
      formid = $currentformid
      console.log(formid)
    }
    if (formid) {
      const frmapiurl = getApiUrl(`forms/${formid}`)
      const fldvalapiurl = getApiUrl(`forms/${formid}/fieldvalues`)
      let res
      try {
        res = await Promise.all([
          fetchWithSession(frmapiurl, {cache: 'no-store'}),
          fetchWithSession(fldvalapiurl, {cache: 'no-store'})
              ])
      } catch (err) {
        console.log(err)
      }
      if (res?.[0]?.ok && res?.[1]?.ok) {
        errorMessage = null
        const formdata = await res[0].json()
        $formlookupresult = formdata
        const fldvaldata = await res[1].json()
        $formlookupfldvals = fldvaldata
        $currentformid = formid
        selectRowFromStore()
      } else {
        $formlookupresult = null
        $formlookupfldvals = null
        errorMessage = 'Error occurred using API'
        console.log(res)
        res?.[0]?.text().then(t => console.log(t))
        res?.[1]?.text().then(t => console.log(t))
      }
    }
  }


  // Ensure storing reactive on only selectedRow.
  $: { storeSelectedRow(selectedRow) }
  function storeSelectedRow(r) {
    console.log('storeSelectedRow')
    if (r) {
      if ($formfldvalselected !== r)
        $formfldvalselected = r
      if ($pagectxstate.selectedRow !== r)
        $pagectxstate.selectedRow = r
      $currentformid = r.formid
    }
  }


  async function selectRowFromStore() {

    console.log('In selectRowFromStore')

    const selected = $pagectxstate.selectedRow ?? $formfldvalselected

    let found = $formlookupfldvals.find(
                      v => isRowMatch(v, selected))

    // If the stored selected is not found, select the first item.
    if (!found && $formlookupfldvals.length > 0) {
      found = $formlookupfldvals[0]
    }
    if (found) {
      console.log(`found.formid: ${found.formid}`)
      // This is the only time we set selectedRow outside of
      // FieldValueTable.
      selectedRow = found

      // Set focus on the corresponding TR if nothing else has focus.
      // TODO: Consider saving focus element (how?) and restoring if
      //       nothing else has the focus, regardless of whether in the
      //       table or not.
      await tick()
      tablewrap?.querySelector('.fieldvaluetable .selected')?.focus()
    }
  }


  onMount( () => {
    mounted = true
    doFormLookup()
  })


  async function handleRowKeydown(e) {
    const subject = $formlookupresult.subject
    let { row, key, isRowSelected } = e.detail
    if (key === 'f' && isRowSelected) {
      // Set up for desired fldval row selection.
      $fldvalselected = { formid: row.formid,
                          formfieldid: row.formfieldid }
      await tick()
      console.log(row.subject, row.fieldname)
      showPage('/fldvals', { subject,
                             field: row.fieldname })
    }
  }


  function handleStaleFormData() {
    doFormLookup()
  }


  const columns = [
    { key: 'seqnum',
      title: 'Seq',
      value: v=>v.fieldseqnum,
      sortable: true },
    { key: 'fieldname',
      title: 'Field',
      value: v=>v.fieldname,
      sortable: true },
    { key: 'alias',
      title: 'Alias',
      value: v=>v.alias,
      sortable: true },
    { key: 'kstr',
      title: 'Value',
      value: v=>v.kstr??'',
      sortable: true,
      class: 'errorindicator' },
    { key: 'fstatus',
      title: 'Status',
      value: v=>v.fstatus??'',
      sortable: true,
      class: 'errorindicator' },
    { key: 'errortype',
      title: 'Error type',
      value: v=>v.errortype??'',
      sortable: true,
      class: 'errorindicator' },
    { key: 'errorstatus',
      title: 'Error status',
      value: v=>v.errorstatus??'',
      sortable: true,
      class: 'errorindicator' },
    { key: 'querycontrol',
      title: 'Query control',
      value: v=>v.querycontrol??'',
      sortable: true,
      class: 'errorindicator' }
  ]

  // Short variable name for convenience
  $: d = $formlookupresult
</script>

<svelte:window
  on:keydown={ e => {
    if (e.key === 'p') {
      const active = document.activeElement
      console.log(active)
      if ( !(['TEXTAREA','INPUT'].includes(active?.tagName)) ) {
        const { subject, namespacename } = $formlookupresult
        //showPage('/subj', { subject, namespace: namespacename })
        showPage('/subj', { subject })
      }
    }
  }} />

{#if errorMessage}
<div>{ errorMessage }</div>
{/if}

<div>

{#if d}
<dl>
  <dt>PID</dt> <dd>{d.subject}</dd>
  <dt>Namespace</dt> <dd>{d.namespacename}</dd>

  <dt>Form name</dt> <dd>{d.shortformname}</dd>
  <dt>Internal form ID</dt> <dd>{d.formid}</dd>
  
  <dt>Repeat instance</dt> <dd>{d.repeatinstance || ''}</dd>
  <dt>Event name</dt> <dd>{d.eventname || ''}</dd>
  
  <dt>External source</dt> <dd>{d.externalname}</dd>
  <dt>REDCap url</dt> <dd>
    <a href="{d.url}" target="_blank" rel="noopener noreferrer">{d.url}</a>  
  </dd>
  <dt>Source type</dt> <dd>{d.sourcetype}</dd>
  <dt>Information request</dt>
    <dd class=inforeqtext>{d.inforeqtext || ''}</dd>


  <dt>Info req status</dt> <dd>{d.inforeqstatus || ''}</dd>
  {#if false}
    <dt>Form error type</dt> <dd>{d.errortype || ''}</dd>
    <dt>Form error text</dt> <dd>{d.errortext || ''}</dd>
    <dt>Form error status</dt> <dd>{d.errorstatus || ''}</dd>
  {/if}
</dl>

<FormActions data={d}
             on:staleformdata={handleStaleFormData}/>

{/if}
</div>
{#if $formlookupfldvals?.length}
  <h3>Form field values</h3>
  <div bind:this={tablewrap}>
  <FormFieldTable rows={ $formlookupfldvals }
                  { columns }
                  bind:selectedRow
                  on:rowkeydown={ handleRowKeydown } />
  </div>
{/if}


<style>
  h3 {
    margin: 1em 0 .5em 0;
  }
  dl {
    display: grid;
    grid-template-columns: fit-content(50%) fit-content(50%) fit-content(50%)  auto;
    column-gap: .5rem;
    font-size: 0;  /* Hack to remove extra space above grid */
  }
  dt, dd {
    font-size: .8rem;
    line-height: 1.5em;
  }
  dt {
    margin: 0 0 0 2em;
    text-align: right;
    font-weight: bold;
  }
  dd {
    margin: 0; 
  }
  dd.inforeqtext {
    grid-column: 2 / span 3
  }
</style>
